/**
 * Attachment review checkpoint shown on the wizard's Preview step
 * whenever the minted link carries files. Proposal links are reused
 * per entry, so files from a previous share silently persist - this
 * card forces a look at the combined list (with duplicate flags and
 * per-row delete) before the flow can continue to the email / link
 * steps.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Trash2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const prettyBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short",
    });
  } catch {
    return "";
  }
};

export default function AttachmentReviewCard({
  token,
  attachments,
  uploadedIds,
  onChanged,
  confirmed,
  onConfirmChange,
}) {
  const [armedId, setArmedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const fresh = new Set(uploadedIds || []);

  const nameCounts = {};
  (attachments || []).forEach((a) => {
    const k = (a.filename || "").toLowerCase();
    nameCounts[k] = (nameCounts[k] || 0) + 1;
  });
  const hasDupes = Object.values(nameCounts).some((n) => n > 1);

  const onDelete = async (att) => {
    setBusyId(att.id);
    try {
      await axios.delete(
        `${API}/api/proposals/share/${token}/attachments/${att.id}`,
      );
      const next = (attachments || []).filter((a) => a.id !== att.id);
      onChanged(next);
      toast.success(`Removed "${att.filename}" from the link.`);
    } catch {
      toast.error(`Couldn't remove "${att.filename}".`);
    } finally {
      setBusyId(null);
      setArmedId(null);
    }
  };

  if (!attachments || attachments.length === 0) return null;

  return (
    <div
      className="border border-amber-200 bg-amber-50/60 rounded-xl p-4 mb-4"
      data-testid="attachment-review-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Paperclip className="w-4 h-4 text-amber-700" />
        <p className="text-xs font-mono tracking-[0.12em] uppercase font-semibold text-amber-900">
          Attached to this link — check before sending
        </p>
      </div>
      <ul className="divide-y divide-amber-100 mb-3">
        {attachments.map((a) => {
          const isDupe = nameCounts[(a.filename || "").toLowerCase()] > 1;
          const isFresh = fresh.has(a.id);
          return (
            <li
              key={a.id}
              className="py-2 flex items-center gap-3"
              data-testid={`attachment-review-row-${a.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {a.filename}
                </p>
                <p className="text-[11px] font-mono text-stone-500">
                  {prettyBytes(a.size_bytes)}
                  {a.uploaded_at ? ` · ${fmtWhen(a.uploaded_at)}` : ""}
                  {a.uploaded_by ? ` · ${a.uploaded_by}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isFresh ? (
                  <span className="text-[10px] font-mono uppercase tracking-wide bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                    added just now
                  </span>
                ) : (
                  <span
                    className="text-[10px] font-mono uppercase tracking-wide bg-stone-200 text-stone-700 px-2 py-0.5 rounded-full"
                    data-testid={`attachment-carried-over-${a.id}`}
                  >
                    from a previous share
                  </span>
                )}
                {isDupe && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full"
                    data-testid={`attachment-duplicate-${a.id}`}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    possible duplicate
                  </span>
                )}
                {armedId === a.id ? (
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => onDelete(a)}
                    data-testid={`attachment-delete-confirm-${a.id}`}
                    className="text-[11px] font-mono uppercase tracking-wide bg-rose-600 hover:bg-rose-700 text-white px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                  >
                    {busyId === a.id ? "Removing…" : "Confirm delete"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setArmedId(a.id)}
                    data-testid={`attachment-delete-${a.id}`}
                    title="Remove this file from the link"
                    className="text-stone-500 hover:text-rose-700 p-1.5 rounded-md hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {hasDupes && (
        <p
          className="text-[11px] text-amber-800 mb-2"
          data-testid="attachment-duplicate-warning"
        >
          Two or more files share the same name — the client would see
          both. Delete the one you don't need.
        </p>
      )}
      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!confirmed}
          onChange={(e) => onConfirmChange(e.target.checked)}
          data-testid="attachment-review-confirm"
          className="mt-0.5 accent-[#1B4F8A]"
        />
        <span className="text-xs text-slate-700 leading-relaxed">
          I&apos;ve checked {attachments.length === 1
            ? "this file"
            : `these ${attachments.length} files`} — {attachments.length === 1 ? "it's" : "they're"} what
          the client should get.
        </span>
      </label>
    </div>
  );
}
